document.addEventListener('alpine:init', () => {
  Alpine.data('tnmBoxOffer', () => ({
    boxes: {}, selected: '', busy: false, message: '', uncertain: false,
    detail: null, detailHandle: '', detailStatus: 'idle', showSticky: false, dialogOpen: false,
    observer: null, attempt: null,
    money(amount, currency) {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style: 'currency', currency}).format(amount);
    },
    photo(url, width = 200) {
      if (!url) return '';
      const image = new URL(url, window.location.origin);
      if (image.protocol !== 'https:' || (image.hostname !== 'cdn.shopify.com' && image.hostname !== window.location.hostname)) return '';
      image.searchParams.set('width', width);
      return image.href;
    },
    async readJson(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, {credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal});
        if (!response.ok) throw new Error('Request unavailable');
        return await response.json();
      } finally { clearTimeout(timer); }
    },
    async fetchBox(handle) {
      const query = `query LandingBox($handle:String!,$country:CountryCode!) @inContext(country:$country) {
        product(handle:$handle) { id title handle availableForSale
          variants(first:1) { nodes { id availableForSale price {amount currencyCode}
            components(first:30) { nodes {quantity productVariant {id availableForSale product {title handle featuredImage {url altText width height}}}} pageInfo {hasNextPage} }
          } pageInfo {hasNextPage} }
        }
      }`;
      const data = await this.readJson('/api/2026-07/graphql.json', {method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify({query, variables: {handle, country: this.$root.dataset.country}})});
      const product = data?.data?.product;
      const variant = product?.variants?.nodes?.[0];
      const components = variant?.components?.nodes;
      const amount = Number(variant?.price?.amount);
      const currency = variant?.price?.currencyCode;
      if (data.errors?.length || product?.handle !== handle || product.variants.pageInfo.hasNextPage || !variant?.id?.match(/^gid:\/\/shopify\/ProductVariant\/\d+$/) || !Number.isFinite(amount) || amount <= 0 || currency !== this.$root.dataset.currency || !Array.isArray(components) || !components.length || variant.components.pageInfo.hasNextPage || components.some(c => !Number.isInteger(c.quantity) || c.quantity < 1 || !c.productVariant?.id || !c.productVariant?.product?.handle || !c.productVariant.product.title)) throw new Error('Box details unavailable');
      return {status:'ready', id:variant.id.split('/').pop(), title:product.title, handle, amount, currency, components,
        available:product.availableForSale && variant.availableForSale && components.every(c => c.productVariant.availableForSale),
        count:components.reduce((total, c) => total + c.quantity, 0),
        signature:JSON.stringify(components.map(c => [c.productVariant.id,c.quantity]).sort())};
    },
    async loadBox(handle) {
      this.boxes[handle] = {status:'loading'};
      try { this.boxes[handle] = await this.fetchBox(handle); }
      catch { this.boxes[handle] = {status:'error'}; }
    },
    async choose(handle) {
      if (this.busy) return;
      if (this.uncertain) { this.dialogOpen = true; this.$refs.review.showModal(); return; }
      this.selected = handle; this.message = ''; this.uncertain = false; this.attempt = null;
      this.dialogOpen = true;
      this.$refs.review.showModal();
      this.busy = true;
      await this.loadBox(handle);
      this.busy = false;
    },
    get chosen() { return this.boxes[this.selected]; },
    async openProduct(handle) {
      this.detailStatus = 'loading'; this.detail = null; this.dialogOpen = true;
      this.detailHandle = /^[a-z0-9-]+$/.test(handle) ? handle : '';
      if (!this.$refs.product.open) this.$refs.product.showModal();
      try {
        if (!/^[a-z0-9-]+$/.test(handle)) throw new Error('Invalid product');
        const product = await this.readJson(this.$root.dataset.root + 'products/' + encodeURIComponent(handle) + '.js');
        if (product.handle !== handle || !product.title || !Array.isArray(product.images)) throw new Error('Product unavailable');
        // Product facts remain merchant-owned. Render description as text, never executable HTML.
        const document = new DOMParser().parseFromString(product.description || '', 'text/html');
        for (const node of document.querySelectorAll('script,style,iframe')) node.remove();
        for (const node of document.querySelectorAll('p,li,h2,h3,br')) node.append('\n');
        this.detail = {title:product.title, handle, images:product.images.slice(0,5), description:document.body.textContent.trim()};
        this.detailStatus = 'ready';
      } catch { this.detailStatus = 'error'; }
    },
    closeDialog(ref) { this.$refs[ref].close(); },
    afterClose() { this.dialogOpen = this.$refs.review.open || this.$refs.product.open; },
    async checkout() {
      if (this.busy || this.uncertain || !this.chosen?.available) return;
      this.busy = true; this.message = '';
      let posted = false;
      try {
        const previous = this.chosen;
        const fresh = await this.fetchBox(this.selected);
        this.boxes[this.selected] = fresh;
        if (!fresh.available) { this.message = 'This box is currently sold out. Please choose another box.'; return; }
        if (fresh.id !== previous.id || fresh.amount !== previous.amount || fresh.currency !== previous.currency || fresh.signature !== previous.signature) {
          this.message = 'This box has changed. Review the updated price and contents, then continue when you’re ready.';
          this.attempt = null; return;
        }
        this.attempt ||= crypto.randomUUID();
        const before = await this.readJson(this.$root.dataset.root + 'cart.js');
        if (!Array.isArray(before.items) || before.currency !== fresh.currency) throw new Error('Cart unavailable');
        const existing = before.items.find(item => item.properties?._tnm_offer_key === this.attempt);
        if (!existing) {
          const properties = {_tnm_offer_key:this.attempt, _tnm_landing:window.location.pathname, _tnm_offer:fresh.handle};
          const params = new URLSearchParams(window.location.search);
          for (const name of ['utm_source','utm_medium','utm_campaign','utm_content']) if (params.get(name)) properties['_' + name] = params.get(name).slice(0,120);
          posted = true;
          await this.readJson(this.$root.dataset.root + 'cart/add.js', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({items:[{id:fresh.id,quantity:1,properties}]})});
        }
        const after = await this.readJson(this.$root.dataset.root + 'cart.js');
        if (!this.verifyCart(after, fresh)) throw new Error('Cart not verified');
        const destination = this.hasUncommittedBuilderBox(after)
          ? this.$root.dataset.root + 'pages/bundle-builder'
          : this.$root.dataset.root + 'checkout';
        if (destination.endsWith('checkout') && (await window.tnmFreeMeatGift?.ensure())?.safe !== true) throw new Error('Free packs not verified');
        window.location.assign(destination);
      } catch {
        if (posted) {
          try {
            const cart = await this.readJson(this.$root.dataset.root + 'cart.js');
            if (this.verifyCart(cart, this.chosen)) {
              const destination = this.hasUncommittedBuilderBox(cart)
                ? this.$root.dataset.root + 'pages/bundle-builder'
                : this.$root.dataset.root + 'checkout';
              if (destination.endsWith('checkout') && (await window.tnmFreeMeatGift?.ensure())?.safe !== true) throw new Error('Free packs not verified');
              window.location.assign(destination);
              return;
            }
            // Shopify cart reads can lag a successful write. Never replay an unconfirmed add.
            this.uncertain = true;
          } catch { this.uncertain = true; }
        }
        this.message = this.uncertain ? 'We couldn’t confirm the final cart. Review checkout before adding another box.' : 'We couldn’t complete that step. Your existing cart is preserved. Please try again.';
      } finally { this.busy = false; }
    },
    verifyCart(cart, box) {
      const line = cart?.items?.find(item => item.properties?._tnm_offer_key === this.attempt);
      return cart.currency === box.currency && cart.item_count > 0 && line?.variant_id === Number(box.id) && line.quantity === 1 && line.has_components === true && line.original_price === Math.round(box.amount * 100) && Number.isInteger(line.final_line_price) && line.final_line_price > 0 && line.final_line_price <= line.original_price;
    },
    hasUncommittedBuilderBox(cartState) {
      if (!cartState || !Array.isArray(cartState.items)) return false;
      if (cartState.items.some(item => item?.properties?._toro_bundle_group != null)) return false;
      try {
        const storage = (typeof window !== 'undefined' && window.localStorage) || null;
        if (!storage) return false;
        const length = Number(storage.length) || 0;
        for (let i = 0; i < length; i++) {
          const key = storage.key(i);
          if (key && typeof key === 'string' && key.startsWith('_bundleStudioSelection:')) {
            const raw = storage.getItem(key);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && Array.isArray(parsed.selection) && parsed.selection.length > 0) {
                const count = parsed.selection.reduce(
                  (sum, item) => sum + (Array.isArray(item) ? (parseInt(item[1], 10) || 0) : 0),
                  0
                );
                if (count > 0) return true;
              }
            }
          }
        }
      } catch (_) {}
      return false;
    },
    init() {
      for (const node of this.$root.querySelectorAll('[data-box-handle]')) this.loadBox(node.dataset.boxHandle);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => { this.showSticky = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0; });
        this.observer.observe(this.$refs.hero);
      }
    },
    destroy() { this.observer?.disconnect(); }
  }));
});
